// MCHD Room Scheduler - Admin Block Management
// Main admin interface for managing time blocks

import { useState } from 'react';
import {
  Container,
  Title,
  Tabs,
  Group,
  Button,
  Stack,
  Alert,
  Text,
  Badge,
  Paper,
  Grid,
  Card,
  ThemeIcon,
  ActionIcon,
  Tooltip,
  Loader,
  Center,
} from '@mantine/core';
import {
  IconPlus,
  IconList,
  IconChartBar,
  IconAlertCircle,
  IconRefresh,
  IconFilter,
  IconSettings,
} from '@tabler/icons-react';

// Import components
import { BlockStats } from '../../../components/organisms/BlockStats';
import { BlockList } from '../../../components/organisms/BlockList';
import { BlockForm } from '../../../components/forms/BlockForm';

// Import hooks
import {
  useBlocks,
  useCreateBlock,
  useDeleteBlock,
  useBlockStats,
} from '../../../hooks/admin/useBlocks';
import { useQueryClient } from '@tanstack/react-query';

// Import types
import type {
  BlockWithStats,
  CreateBlockData,
} from '../../../api/endpoints/blocks';

// =============================================================================
// INTERFACES
// =============================================================================

interface BlockFilters {
  search: string;
  isActive?: boolean | undefined;
  startDate?: string | undefined;
  endDate?: string | undefined;
  sortBy: 'name' | 'startDate' | 'endDate' | 'createdAt' | 'updatedAt';
  sortOrder: 'asc' | 'desc';
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Admin Block Management Component
 * Provides comprehensive block management interface for administrators
 */
export function BlockManagement(): JSX.Element {
  // =============================================================================
  // STATE
  // =============================================================================

  const [activeTab, setActiveTab] = useState<string>('overview');
  const [selectedBlock, setSelectedBlock] = useState<BlockWithStats | null>(null);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [filters, setFilters] = useState<BlockFilters>({
    search: '',
    sortBy: 'name',
    sortOrder: 'asc'
  });

  // =============================================================================
  // HOOKS
  // =============================================================================

  // Query client for manual cache invalidation
  const queryClient = useQueryClient();

  // Data hooks
  const { data: blocksData, isLoading: blocksLoading, error: blocksError } = useBlocks({
    ...(filters.search && { search: filters.search }),
    ...(filters.isActive !== undefined && { isActive: filters.isActive }),
    ...(filters.startDate && { startDate: filters.startDate }),
    ...(filters.endDate && { endDate: filters.endDate }),
    sortBy: filters.sortBy,
    sortOrder: filters.sortOrder
  });

  const { data: statsData, isLoading: statsLoading, error: statsError } = useBlockStats({
    ...(filters.startDate && { startDate: filters.startDate }),
    ...(filters.endDate && { endDate: filters.endDate })
  });

  // Mutation hooks
  const createBlockMutation = useCreateBlock();
  const deleteBlockMutation = useDeleteBlock();

  // =============================================================================
  // HANDLERS
  // =============================================================================

  /**
   * Handle filter changes
   */
  const handleFilterChange = (newFilters: Partial<BlockFilters>) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  /**
   * Handle create block
   */
  const handleCreateBlock = (data: CreateBlockData) => {
    createBlockMutation.mutate(data, {
      onSuccess: () => {
        setShowForm(false);
        setSelectedBlock(null);
      }
    });
  };

  /**
   * Handle update block
   */
  const handleUpdateBlock = (id: string, data: Partial<CreateBlockData>) => {
    // This will be implemented when we add the update mutation
    console.log('Update block:', id, data);
  };

  /**
   * Handle delete block
   */
  const handleDeleteBlock = (id: string) => {
    deleteBlockMutation.mutate(id);
  };

  /**
   * Handle select block
   */
  const handleSelectBlock = (block: BlockWithStats) => {
    setSelectedBlock(block);
    setShowForm(true);
    setActiveTab('form');
  };

  /**
   * Handle close form
   */
  const handleCloseForm = () => {
    setShowForm(false);
    setSelectedBlock(null);
    setActiveTab('overview');
  };

  /**
   * Handle refresh data
   */
  const handleRefresh = () => {
    // Force refetch of all block-related queries
    queryClient.invalidateQueries({ queryKey: ['blocks'] });
  };

  // =============================================================================
  // RENDER
  // =============================================================================

  return (
    <Container size="xl" py="md">
      {/* Header */}
      <Group justify="space-between" mb="lg">
        <div>
          <Title order={2} c="blue.8">
            Block Management
          </Title>
          <Text c="dimmed" size="sm">
            Manage time blocks and scheduling periods
          </Text>
        </div>
        <Group>
          <Tooltip label="Refresh data">
            <ActionIcon
              variant="light"
              color="blue"
              onClick={handleRefresh}
              loading={blocksLoading}
            >
              <IconRefresh size={16} />
            </ActionIcon>
          </Tooltip>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => {
              setSelectedBlock(null);
              setShowForm(true);
              setActiveTab('form');
            }}
            loading={createBlockMutation.isPending}
          >
            Create Block
          </Button>
        </Group>
      </Group>

      {/* Error Alert */}
      {(blocksError || statsError) && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error Loading Data"
          color="red"
          mb="md"
        >
          {blocksError?.message || statsError?.message || 'An error occurred while loading data'}
        </Alert>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onChange={(value) => setActiveTab(value || 'overview')}>
        <Tabs.List>
          <Tabs.Tab
            value="overview"
            leftSection={<IconChartBar size={16} />}
          >
            Overview
          </Tabs.Tab>
          <Tabs.Tab
            value="list"
            leftSection={<IconList size={16} />}
          >
            Blocks
          </Tabs.Tab>
          {showForm && (
            <Tabs.Tab
              value="form"
              leftSection={<IconSettings size={16} />}
            >
              {selectedBlock ? 'Edit Block' : 'Create Block'}
            </Tabs.Tab>
          )}
        </Tabs.List>

        {/* Overview Tab */}
        <Tabs.Panel value="overview" pt="lg">
          <Stack gap="md">
            <BlockStats
              {...(statsData?.data && { stats: statsData.data })}
              {...(statsLoading !== undefined && { isLoading: statsLoading })}
              {...(statsError && { error: statsError })}
            />
            
            {/* Quick Actions */}
            <Paper p="md" withBorder>
              <Title order={4} mb="md">Quick Actions</Title>
              <Grid>
                <Grid.Col span={{ base: 6, md: 3 }}>
                  <Card withBorder p="sm" style={{ cursor: 'pointer' }} onClick={() => setActiveTab('list')}>
                    <Group>
                      <ThemeIcon color="blue" variant="light">
                        <IconList size={16} />
                      </ThemeIcon>
                      <div>
                        <Text size="sm" fw={500}>View All</Text>
                        <Text size="xs" c="dimmed">Browse blocks</Text>
                      </div>
                    </Group>
                  </Card>
                </Grid.Col>
                <Grid.Col span={{ base: 6, md: 3 }}>
                  <Card withBorder p="sm" style={{ cursor: 'pointer' }} onClick={() => {
                    setSelectedBlock(null);
                    setShowForm(true);
                    setActiveTab('form');
                  }}>
                    <Group>
                      <ThemeIcon color="green" variant="light">
                        <IconPlus size={16} />
                      </ThemeIcon>
                      <div>
                        <Text size="sm" fw={500}>Create New</Text>
                        <Text size="xs" c="dimmed">Add block</Text>
                      </div>
                    </Group>
                  </Card>
                </Grid.Col>
                <Grid.Col span={{ base: 6, md: 3 }}>
                  <Card withBorder p="sm" style={{ cursor: 'pointer' }} onClick={handleRefresh}>
                    <Group>
                      <ThemeIcon color="orange" variant="light">
                        <IconRefresh size={16} />
                      </ThemeIcon>
                      <div>
                        <Text size="sm" fw={500}>Refresh</Text>
                        <Text size="xs" c="dimmed">Update data</Text>
                      </div>
                    </Group>
                  </Card>
                </Grid.Col>
                <Grid.Col span={{ base: 6, md: 3 }}>
                  <Card withBorder p="sm" style={{ cursor: 'pointer' }} onClick={() => setFilters(prev => ({ ...prev, isActive: true }))}>
                    <Group>
                      <ThemeIcon color="teal" variant="light">
                        <IconFilter size={16} />
                      </ThemeIcon>
                      <div>
                        <Text size="sm" fw={500}>Active Only</Text>
                        <Text size="xs" c="dimmed">Filter active</Text>
                      </div>
                    </Group>
                  </Card>
                </Grid.Col>
              </Grid>
            </Paper>

            {/* Recent Blocks */}
            <Paper p="md" withBorder>
              <Title order={4} mb="md">Recent Blocks</Title>
              {blocksLoading ? (
                <Center p="xl">
                  <Loader size="md" />
                </Center>
              ) : blocksData?.data?.blocks?.length ? (
                <Stack gap="sm">
                  {blocksData.data.blocks.slice(0, 5).map((block) => (
                    <Card key={block.id} withBorder p="sm" style={{ cursor: 'pointer' }} onClick={() => handleSelectBlock(block)}>
                      <Group justify="space-between">
                        <div>
                          <Text fw={500}>{block.name}</Text>
                          <Text size="xs" c="dimmed">
                            {new Date(block.startDate).toLocaleDateString()} - {new Date(block.endDate).toLocaleDateString()}
                          </Text>
                        </div>
                        <Badge color={block.isActive ? 'green' : 'red'} size="sm">
                          {block.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </Group>
                    </Card>
                  ))}
                </Stack>
              ) : (
                <Text c="dimmed" ta="center" py="xl">
                  No blocks found
                </Text>
              )}
            </Paper>
          </Stack>
        </Tabs.Panel>

        {/* List Tab */}
        <Tabs.Panel value="list" pt="lg">
          <BlockList
            blocks={blocksData?.data?.blocks || []}
            pagination={blocksData?.data?.pagination}
            isLoading={blocksLoading}
            error={blocksError}
            filters={filters}
            onFilterChange={handleFilterChange as any}
            onSelectBlock={handleSelectBlock}
            onDeleteBlock={handleDeleteBlock}
            onUpdateBlock={handleUpdateBlock}
          />
        </Tabs.Panel>

        {/* Form Tab */}
        {showForm && (
          <Tabs.Panel value="form" pt="lg">
            <BlockForm
              block={selectedBlock}
              onSubmit={handleCreateBlock}
              onCancel={handleCloseForm}
              isLoading={createBlockMutation.isPending}
            />
          </Tabs.Panel>
        )}
      </Tabs>
    </Container>
  );
}

export default BlockManagement;
